/**
 * Raster scale for cropped project-file drawings.
 *
 * PDF MediaBox is physical paper (user units = 1/72 in). Every crop on a
 * page uses the same DPI so 1 mm of paper is the same number of pixels.
 * Printed `1:N` may be wrong; paper millimetres and pixels are always true.
 * {@link pixelsPerMeterFromPaper} is the conversion *if* the stamp is trusted.
 */

import type {
  DimensionRead,
  MetreScaleClaim,
  MetreScaleSource,
  PaperClaim,
  PaperClaimSource,
  ScaleBarRead,
  ScaleSource,
} from '../models/project-file-drawing.model';
import { assertUnreachable } from './function.helpers';

export const PDF_POINTS_PER_INCH = 72;
export const MM_PER_INCH = 25.4;
/** Scan-like density for vector PDFs. */
export const TARGET_DRAWING_RASTER_DPI = 200;
/** Memory cap on the page long edge (A0 at 200 DPI would exceed this). */
export const MAX_DRAWING_RASTER_EDGE_PX = 8192;

export type PdfPageRasterScale = {
  /** pdf.js viewport scale (`1` = 72 DPI). */
  scale: number;
  /** Actual DPI after the page-edge cap. */
  dpi: number;
};

/**
 * pdf.js scale so the page is {@link TARGET_DRAWING_RASTER_DPI}, unless the
 * long edge would exceed {@link MAX_DRAWING_RASTER_EDGE_PX}.
 * {@link pageWidthPts} / {@link pageHeightPts} are MediaBox at scale 1.
 */
export const pdfPageRasterScale = (
  pageWidthPts: number,
  pageHeightPts: number,
): PdfPageRasterScale | null => {
  const longPts = Math.max(pageWidthPts, pageHeightPts);
  if (!(longPts > 0)) return null;
  const inches = longPts / PDF_POINTS_PER_INCH;
  const dpi = Math.min(
    TARGET_DRAWING_RASTER_DPI,
    MAX_DRAWING_RASTER_EDGE_PX / inches,
  );
  if (!(dpi > 0)) return null;
  return { scale: dpi / PDF_POINTS_PER_INCH, dpi };
};

/** Convert PDF user units (scale-1 viewport) to millimetres. */
export const pdfPointsToMm = (pts: number): number =>
  (pts / PDF_POINTS_PER_INCH) * MM_PER_INCH;

/**
 * ISO A and the extended sheets used on drawings (2A0, 4A0, 8A0).
 * Short edge first. Matched on millimetres, not on aspect: every A sheet
 * has the same aspect, so aspect alone always lands on the largest.
 */
const ISO_PAGE_PAPER_MM: readonly {
  label: string;
  short: number;
  long: number;
}[] = [
  { label: '8A0', short: 2378, long: 3364 },
  { label: '4A0', short: 1682, long: 2378 },
  { label: '2A0', short: 1189, long: 1682 },
  { label: 'A0', short: 841, long: 1189 },
  { label: 'A1', short: 594, long: 841 },
  { label: 'A2', short: 420, long: 594 },
  { label: 'A3', short: 297, long: 420 },
  { label: 'A4', short: 210, long: 297 },
  { label: 'A5', short: 148, long: 210 },
];

/** Long edge of a named sheet in millimetres. Null for a name that is not a sheet. */
export const paperLongEdgeMm = (label: string): number | null =>
  ISO_PAGE_PAPER_MM.find((paper) => paper.label === label.toUpperCase())
    ?.long ?? null;

/** A crop or a bleed can sit a little inside the media box. */
const PAGE_PAPER_TOLERANCE = 0.05;
/**
 * Farther than this, the page is not a sheet. No measurement claim is made.
 * Between this and {@link PAGE_PAPER_TOLERANCE} the hit is a nearest size.
 */
const NEAREST_EDGE_LIMIT = 0.2;

const PAPER_TOKEN_SOURCE = String.raw`[248]A0|A[0-5]`;

/**
 * Method weights. Same for every file. A printed parenthesis is the
 * measurement paper when the sheet prints one. That score is higher than a
 * nearest page. A page measurement, including a nearest size, wins on its
 * score when no parenthesis was printed, including when it is the only claim.
 * A token that is the same on every sheet, and a token in the file name, are
 * recorded and not chosen. Extent is the weakest, and only when nothing is
 * printed.
 */
const PAPER_METHOD_WEIGHT: Record<PaperClaimSource, number> = {
  scale_parenthesis: 6,
  page_measurement: 8,
  title_block_token: 2,
  drawing_extent: 1,
  repeated_sheet: 0,
  file_name: 0,
};

type PaperSource = PaperClaimSource;

const EXACT_CLEANLINESS = 1;
const NEAREST_CLEANLINESS = 0.5;
const UNMARKED_TOKEN_CLEANLINESS = 0.5;
const EXTENT_CLEANLINESS = 0.5;

/** Lower score is kept when it is at least this fraction of the winner. */
const CLOSE_SCORE_NUMERATOR = 3;
const CLOSE_SCORE_DENOMINATOR = 4;

const SOURCE_RANK: Record<PaperSource, number> = {
  scale_parenthesis: 0,
  page_measurement: 1,
  title_block_token: 2,
  drawing_extent: 3,
  repeated_sheet: 4,
  file_name: 5,
};

export type PaperExtentM = {
  widthM: number;
  heightM: number;
};

export type PaperSizeInput = {
  scale: string | null;
  page: { widthMm: number | null; heightMm: number | null };
  /** Real-world plan size. Omitted when no step measured it. */
  extentM?: PaperExtentM | null;
  /** Title-block text other than the scale line. Omitted when unread. */
  stampText?: string | null;
  /** File name. Omitted when the file has none. A token here is not chosen. */
  fileName?: string | null;
  /**
   * Paper token shared by every sheet's drawing number.
   * Omitted when the sheets do not share one.
   */
  repeatedSheetTokens?: readonly string[] | null;
};

export type DrawingScalePaper = {
  scale: string | null;
  scaleSource: ScaleSource | null;
  paper: string | null;
  paperSource: PaperClaimSource | null;
  paperClaims: PaperClaim[];
};

const makeClaim = (
  source: PaperSource,
  value: string | null,
  cleanliness: number,
  millimetres?: { widthMm: number; heightMm: number },
): PaperClaim => {
  const methodWeight = PAPER_METHOD_WEIGHT[source];
  return {
    source,
    value,
    cleanliness,
    methodWeight,
    score: cleanliness * methodWeight,
    ...millimetres,
  };
};

const sheetEdgeError = (
  widthMm: number,
  heightMm: number,
  paper: { short: number; long: number },
): number => {
  const portrait = Math.max(
    Math.abs(widthMm - paper.short) / paper.short,
    Math.abs(heightMm - paper.long) / paper.long,
  );
  const landscape = Math.max(
    Math.abs(widthMm - paper.long) / paper.long,
    Math.abs(heightMm - paper.short) / paper.short,
  );
  return Math.min(portrait, landscape);
};

const measurementCleanliness = (error: number): number | null => {
  if (error <= PAGE_PAPER_TOLERANCE) return EXACT_CLEANLINESS;
  if (error <= NEAREST_EDGE_LIMIT) return NEAREST_CLEANLINESS;
  return null;
};

const measurementClaim = (
  widthMm: number | null,
  heightMm: number | null,
): PaperClaim | null => {
  if (widthMm == null || heightMm == null) return null;
  if (!(widthMm > 0) || !(heightMm > 0)) return null;
  let best: { label: string; error: number } | null = null;
  for (const paper of ISO_PAGE_PAPER_MM) {
    const error = sheetEdgeError(widthMm, heightMm, paper);
    if (best != null && error >= best.error) continue;
    best = { label: paper.label, error };
  }
  if (best == null) return null;
  const cleanliness = measurementCleanliness(best.error);
  if (cleanliness == null) return null;
  return makeClaim('page_measurement', best.label, cleanliness);
};

/**
 * Paper name when both edges are already that sheet. Null when the media
 * box is not a real size — aspect is not used, and a drawing-number token
 * is not a size. A nearest size is not this label.
 */
export const pagePaperLabel = (
  widthMm: number,
  heightMm: number,
): string | null => {
  const claim = measurementClaim(widthMm, heightMm);
  if (claim == null || claim.cleanliness < EXACT_CLEANLINESS) return null;
  return claim.value;
};

const barePaperRe = (): RegExp =>
  new RegExp(
    String.raw`(?<![A-Za-z0-9])(?:${PAPER_TOKEN_SOURCE})(?![A-Za-z0-9-])`,
    'gi',
  );

const paperListRe = (): RegExp =>
  new RegExp(
    String.raw`^\s*(?:(?:${PAPER_TOKEN_SOURCE})\s*,\s*)*(?:${PAPER_TOKEN_SOURCE})\s*$`,
    'i',
  );

const parenPaperGroupRe = (): RegExp =>
  new RegExp(
    String.raw`\(\s*(?:(?:${PAPER_TOKEN_SOURCE})\s*,\s*)*(?:${PAPER_TOKEN_SOURCE})\s*\)`,
    'gi',
  );

const paperListTokens = (inner: string): string[] | null => {
  if (!paperListRe().test(inner)) return null;
  return [...inner.matchAll(new RegExp(PAPER_TOKEN_SOURCE, 'gi'))].map(
    (match) => match[0].toUpperCase(),
  );
};

type TextSpan = { start: number; end: number };

const parenthesisPapers = (
  text: string,
): { papers: string[]; spans: TextSpan[] } => {
  const papers: string[] = [];
  const spans: TextSpan[] = [];
  for (const match of text.matchAll(/\(([^)]*)\)/g)) {
    const inner = match[1];
    const start = match.index;
    if (inner == null || start == null) continue;
    const tokens = paperListTokens(inner);
    if (tokens == null) continue;
    papers.push(...tokens);
    spans.push({ start, end: start + match[0].length });
  }
  return { papers, spans };
};

const spanOccupied = (index: number, spans: readonly TextSpan[]): boolean =>
  spans.some((span) => index >= span.start && index < span.end);

/** Paper tokens outside a parenthesis list. A drawing number (`A4-01`) is not one. */
const barePaperTokens = (
  text: string,
  occupied: readonly TextSpan[],
): string[] => {
  const tokens: string[] = [];
  for (const match of text.matchAll(barePaperRe())) {
    const start = match.index;
    if (start == null || spanOccupied(start, occupied)) continue;
    tokens.push(match[0].toUpperCase());
  }
  return tokens;
};

const pushClaim = (claims: PaperClaim[], claim: PaperClaim | null): void => {
  if (claim == null) return;
  const seen = claims.some(
    (item) => item.source === claim.source && item.value === claim.value,
  );
  if (seen) return;
  claims.push(claim);
};

const smallestContainingSheet = (
  widthMm: number,
  heightMm: number,
): string | null => {
  const short = Math.min(widthMm, heightMm);
  const long = Math.max(widthMm, heightMm);
  const matches = ISO_PAGE_PAPER_MM.filter(
    (paper) => short <= paper.short && long <= paper.long,
  );
  return matches[matches.length - 1]?.label ?? null;
};

/**
 * Smallest known sheet that would hold the plan at the printed scale.
 * Null when the extent or the scale is missing, or when the plan fits none.
 */
const extentClaim = (
  scale: string | null,
  extentM: PaperExtentM | null | undefined,
): PaperClaim | null => {
  if (extentM == null) return null;
  if (!(extentM.widthM > 0) || !(extentM.heightM > 0)) return null;
  const denominator = parsePrintedScaleDenominator(scale);
  if (denominator == null) return null;
  const widthMm = (extentM.widthM * 1000) / denominator;
  const heightMm = (extentM.heightM * 1000) / denominator;
  const label = smallestContainingSheet(widthMm, heightMm);
  if (label == null) return null;
  return makeClaim('drawing_extent', label, EXTENT_CLEANLINESS);
};

const pageMeasurementClaim = (
  widthMm: number | null,
  heightMm: number | null,
): PaperClaim | null => {
  if (widthMm == null || heightMm == null) return null;
  if (!(widthMm > 0) || !(heightMm > 0)) return null;
  const labelled = measurementClaim(widthMm, heightMm);
  if (labelled == null) {
    return makeClaim('page_measurement', null, 0, { widthMm, heightMm });
  }
  return { ...labelled, widthMm, heightMm };
};

/** Paper token bounded by a non-letter, including a hyphen in a file name. */
const identifierPaperRe = (): RegExp =>
  new RegExp(
    String.raw`(?<![A-Za-z0-9])(?:${PAPER_TOKEN_SOURCE})(?![A-Za-z0-9])`,
    'gi',
  );

/** Paper tokens in a file name or drawing number. A scale parenthesis is not one. */
export const identifierPaperTokens = (
  text: string | null | undefined,
): string[] => {
  if (!text) return [];
  const tokens = [...text.matchAll(identifierPaperRe())].map((match) =>
    match[0].toUpperCase(),
  );
  return [...new Set(tokens)];
};

/**
 * One claim per source that actually read a value. A missing source adds none.
 * Extent is omitted when a paper size is already printed.
 */
export const paperSizeClaims = (input: PaperSizeInput): PaperClaim[] => {
  const claims: PaperClaim[] = [];
  pushClaim(
    claims,
    pageMeasurementClaim(input.page.widthMm, input.page.heightMm),
  );
  const scale = input.scale ?? '';
  const parenthesis = parenthesisPapers(scale);
  for (const paper of parenthesis.papers) {
    pushClaim(claims, makeClaim('scale_parenthesis', paper, EXACT_CLEANLINESS));
  }
  const stamp = input.stampText ?? '';
  const tokens = [
    ...barePaperTokens(scale, parenthesis.spans),
    ...barePaperTokens(stamp, []),
  ];
  for (const paper of tokens) {
    pushClaim(
      claims,
      makeClaim('title_block_token', paper, UNMARKED_TOKEN_CLEANLINESS),
    );
  }
  for (const paper of input.repeatedSheetTokens ?? []) {
    pushClaim(claims, makeClaim('repeated_sheet', paper, EXACT_CLEANLINESS));
  }
  for (const paper of identifierPaperTokens(input.fileName)) {
    pushClaim(claims, makeClaim('file_name', paper, EXACT_CLEANLINESS));
  }
  const printed = parenthesis.papers.length > 0 || tokens.length > 0;
  if (!printed) pushClaim(claims, extentClaim(input.scale, input.extentM));
  return claims;
};

const scoresAreClose = (lower: number, higher: number): boolean =>
  lower * CLOSE_SCORE_DENOMINATOR >= higher * CLOSE_SCORE_NUMERATOR;

type RankedPaper = {
  value: string;
  source: PaperSource;
  score: number;
  sourceRank: number;
  seen: number;
};

const claimCanWin = (claim: PaperClaim): boolean => {
  if (claim.value == null) return false;
  switch (claim.source) {
    case 'scale_parenthesis':
    case 'repeated_sheet':
    case 'file_name':
      return false;
    case 'page_measurement':
      return true;
    case 'title_block_token':
    case 'drawing_extent':
      return true;
    default:
      return assertUnreachable(claim.source);
  }
};

/**
 * Measurement paper. A printed parenthesis wins when it is present, because
 * its score is higher than a nearest page. Otherwise the highest score wins,
 * so a nearest page can win, including when it is the only claim. Two close
 * scores are both kept. Every claim stays stored.
 */
const chosenMeasurementPaper = (
  claims: readonly PaperClaim[],
): { value: string; source: PaperSource } | null => {
  const parenthesis = [
    ...new Set(
      claims
        .filter(
          (claim) =>
            claim.source === 'scale_parenthesis' && claim.value != null,
        )
        .map((claim) => claim.value ?? ''),
    ),
  ].filter((value) => value !== '');
  if (parenthesis.length > 1) return null;
  const printed = parenthesis[0];
  if (printed != null) return { value: printed, source: 'scale_parenthesis' };

  const ranked = new Map<string, RankedPaper>();
  claims.forEach((claim, seen) => {
    if (!claimCanWin(claim) || claim.value == null) return;
    const current = ranked.get(claim.value);
    const sourceRank = SOURCE_RANK[claim.source];
    if (current == null) {
      ranked.set(claim.value, {
        value: claim.value,
        source: claim.source,
        score: claim.score,
        sourceRank,
        seen,
      });
      return;
    }
    if (claim.score < current.score) return;
    if (claim.score === current.score && sourceRank >= current.sourceRank) {
      return;
    }
    ranked.set(claim.value, {
      value: claim.value,
      source: claim.source,
      score: claim.score,
      sourceRank,
      seen: current.seen,
    });
  });
  const ordered = [...ranked.values()].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.sourceRank !== b.sourceRank) return a.sourceRank - b.sourceRank;
    return a.seen - b.seen;
  });
  const winner = ordered[0];
  if (winner == null) return null;
  const contested = ordered.some(
    (item) =>
      item.value !== winner.value && scoresAreClose(item.score, winner.score),
  );
  if (contested) return null;
  return { value: winner.value, source: winner.source };
};

const stripPaperTokens = (scale: string): string =>
  scale
    .replace(parenPaperGroupRe(), '')
    .replace(barePaperRe(), '')
    .replace(/\(\s*\)/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+,/g, ',')
    .trim();

const ratioOnly = (scale: string | null): string | null => {
  const stripped = stripPaperTokens(scale?.trim() ?? '');
  return stripped === '' ? null : stripped;
};

/**
 * A paper token in a scale stamp (`1:50 (A3)`, `1:50 8A0`).
 * `A2` inside a drawing number (`A2-01`) is not paper.
 */
const PRINTED_PAPER_RE = new RegExp(
  String.raw`(?<![A-Za-z0-9])(${PAPER_TOKEN_SOURCE})(?![A-Za-z0-9-])`,
  'i',
);

/** Paper named in a scale string. Null when the stamp names none. */
export const printedPaperLabel = (
  scale: string | null | undefined,
): string | null => {
  const token = scale?.match(PRINTED_PAPER_RE)?.[1];
  if (token == null) return null;
  return token.toUpperCase();
};

/**
 * Splits a printed scale into the ratio and the measurement paper.
 * The ratio stays a scale string. Paper and every other claim that was read
 * stay separate. A missing source adds no claim.
 */
export const scaleWithPagePaper = (
  scale: string | null,
  page: { widthMm: number | null; heightMm: number | null },
  sources?: Omit<PaperSizeInput, 'scale' | 'page'>,
): DrawingScalePaper => {
  const paperClaims = paperSizeClaims({ scale, page, ...sources });
  const chosen = chosenMeasurementPaper(paperClaims);
  const ratio = ratioOnly(scale);
  return {
    scale: ratio,
    scaleSource:
      parsePrintedScaleDenominator(ratio) == null ? null : 'printed_scale',
    paper: chosen?.value ?? null,
    paperSource: chosen?.source ?? null,
    paperClaims,
  };
};

/**
 * Printed `1:N` denominator (N). Null when the stamp is missing or not `1:N`.
 */
export const parsePrintedScaleDenominator = (
  raw: string | null | undefined,
): number | null => {
  if (!raw) return null;
  const match = raw.match(/(?:^|[^\d])1\s*[:/]\s*(\d{1,4})\b/i);
  if (!match?.[1]) return null;
  const denominator = Number(match[1]);
  if (!Number.isFinite(denominator) || denominator < 1) return null;
  return denominator;
};

/**
 * Pixels along one metre in real-world units, if printed scale `1:N` is true.
 * Uses crop JPEG width vs its paper width (same ratio as height when square pixels).
 */
export const pixelsPerMeterFromPaper = (
  pixelWidth: number,
  paperWidthMm: number,
  scaleDenominator: number,
): number | null => {
  if (!(pixelWidth > 0) || !(paperWidthMm > 0) || !(scaleDenominator > 0)) {
    return null;
  }
  return (pixelWidth / paperWidthMm / scaleDenominator) * 1000;
};

/** A bar shorter than this on the raster is too short to measure. */
const MIN_SCALE_BAR_PX = 20;

/**
 * Raster pixels along one metre from a printed scale bar. The bar is drawn at
 * the drawing's scale, so it holds whatever the sheet was printed or exported
 * at. Null when the bar is too short to measure or its length is not positive.
 */
export const pixelsPerMeterFromScaleBar = (
  bar: ScaleBarRead,
  page: { widthPx: number; heightPx: number },
): number | null => {
  if (!(bar.end_m > 0)) return null;
  const lengthPx = Math.hypot(
    (bar.end_x - bar.zero_x) * page.widthPx,
    (bar.end_y - bar.zero_y) * page.heightPx,
  );
  if (!(lengthPx >= MIN_SCALE_BAR_PX)) return null;
  return lengthPx / bar.end_m;
};

/**
 * Lower wins. The scale bar is drawn with the drawing, so it holds when the
 * sheet was exported or printed at another size; paper and ratio hold only
 * when the page kept its paper size.
 */
const METRE_SCALE_SOURCE_RANK: Record<MetreScaleSource, number> = {
  scale_bar: 0,
  dimensions: 1,
  paper_and_ratio: 2,
};

/** Printed dimensions within 3 % of each other give the same scale. */
const DIMENSIONS_AGREE = 0.03;

/**
 * Pixels per metre from the printed dimensions of one drawing. Every
 * dimension on a sheet is drawn at the same scale, so the largest group that
 * agrees is the reading; a dimension outside it is a misread. Null unless at
 * least two agree. The median of the group.
 */
export const pixelsPerMeterFromDimensions = (
  dimensions: readonly DimensionRead[],
  page: { widthPx: number; heightPx: number },
): number | null => {
  const readings = dimensions.flatMap((dimension) => {
    const value = pixelsPerMeterFromScaleBar(
      {
        zero_x: dimension.start_x,
        zero_y: dimension.start_y,
        end_x: dimension.end_x,
        end_y: dimension.end_y,
        end_m: dimension.length_mm / 1000,
      },
      page,
    );
    return value === null ? [] : [value];
  });
  const groups = readings.map((center) =>
    readings
      .filter(
        (value) =>
          Math.abs(value - center) <=
          DIMENSIONS_AGREE * Math.max(value, center),
      )
      .sort((a, b) => a - b),
  );
  const largest = groups.reduce<number[]>(
    (best, group) => (group.length > best.length ? group : best),
    [],
  );
  if (largest.length < 2) return null;
  const middle = Math.floor(largest.length / 2);
  return largest.length % 2 === 1
    ? (largest[middle] ?? null)
    : ((largest[middle - 1] ?? 0) + (largest[middle] ?? 0)) / 2;
};

/** Every pixels-per-metre reading one drawing has. */
export const metreScaleClaims = (input: {
  scaleBar: ScaleBarRead | null;
  dimensions?: readonly DimensionRead[];
  page: { widthPx: number; heightPx: number };
  pixelWidth: number;
  paperWidthMm: number | null;
  scaleDenominator: number | null;
}): MetreScaleClaim[] => {
  const fromBar =
    input.scaleBar === null
      ? null
      : pixelsPerMeterFromScaleBar(input.scaleBar, input.page);
  const fromDimensions = pixelsPerMeterFromDimensions(
    input.dimensions ?? [],
    input.page,
  );
  const fromPaper =
    input.paperWidthMm === null || input.scaleDenominator === null
      ? null
      : pixelsPerMeterFromPaper(
          input.pixelWidth,
          input.paperWidthMm,
          input.scaleDenominator,
        );
  return [
    ...(fromBar === null
      ? []
      : [{ source: 'scale_bar' as const, pixelsPerMeter: fromBar }]),
    ...(fromDimensions === null
      ? []
      : [{ source: 'dimensions' as const, pixelsPerMeter: fromDimensions }]),
    ...(fromPaper === null
      ? []
      : [{ source: 'paper_and_ratio' as const, pixelsPerMeter: fromPaper }]),
  ];
};

/** The pixels per metre of the best-ranked source, or null without one. */
export const chosenPixelsPerMeter = (
  claims: readonly MetreScaleClaim[],
): number | null =>
  [...claims].sort(
    (a, b) =>
      METRE_SCALE_SOURCE_RANK[a.source] - METRE_SCALE_SOURCE_RANK[b.source],
  )[0]?.pixelsPerMeter ?? null;

/**
 * Downscale a crop so its long edge is at most {@link MAX_DRAWING_RASTER_EDGE_PX}.
 * Never upscales.
 */
export const destSizeForRasterCrop = (
  srcWidth: number,
  srcHeight: number,
): { width: number; height: number } => {
  const edge = Math.max(srcWidth, srcHeight);
  if (!(edge > 0)) return { width: 1, height: 1 };
  if (edge <= MAX_DRAWING_RASTER_EDGE_PX) {
    return { width: srcWidth, height: srcHeight };
  }
  const scale = MAX_DRAWING_RASTER_EDGE_PX / edge;
  return {
    width: Math.max(1, Math.round(srcWidth * scale)),
    height: Math.max(1, Math.round(srcHeight * scale)),
  };
};
